/**
 * Block 5.1 (2026-05-26): Month-Close Runner API.
 */
import { api } from './client';
import type { Page } from './catalogs';


export type OperationType =
  | 'AMORTIZATION'
  | 'PAYROLL'
  | 'FX_REVALUATION'
  // Block 8.2 ФОП єдинник
  | 'SINGLE_TAX'
  | 'FOP_SELF_ESV'
  | 'FOP_SELF_WZ'
  // Block 11 ФОП ЗСО
  | 'FOP_ZSO_QUARTERLY'
  // Block 5.2 closing operations
  | 'EXPENSE_CLOSING'
  | 'REVENUE_CLOSING'
  | 'QUARTERLY_SNAPSHOT'
  | 'FINANCIAL_RESULT_CLOSING'
  // ч.1134: річне закриття рахунку 443 (використаний прибуток)
  | 'USED_PROFIT_CLOSING'
  // Виробнича собівартість і розподіл загальновиробничих
  | 'PRODUCTION_COST_CLOSING'
  | 'OVERHEAD_ALLOCATION'
  // ч.1120: зароблені за місяць дні відпустки
  | 'VACATION_ACCRUAL';

export type OperationStatus = 'SUCCESS' | 'FAILED' | 'SKIPPED';

export type MonthCloseStatus =
  | 'PENDING' | 'IN_PROGRESS' | 'COMPLETED'
  | 'PARTIAL' | 'FAILED' | 'CANCELLED';


export interface OperationResult {
  operation_type: OperationType;
  status: OperationStatus;
  document_id: string | null;
  started_at: string;
  duration_ms: number;
  error_message: string | null;
  summary: Record<string, unknown>;
}

export interface PreCheckWarning {
  warning_type: string;
  /** ч.1029: BLOCKING — закривати не можна; INFO — можна, але бухгалтер має знати. */
  severity?: 'BLOCKING' | 'INFO';
  title?: string;
  hint?: string;
  count: number;
  items: Array<Record<string, unknown>>;
}

// Block 5.2 (2026-05-27): Closing snapshot computed з operations_results.
export interface ClosingSnapshot {
  expense_closing: {
    total_amount?: string;
    entries_count?: number;
    subconto_breakdown?: Array<{
      account_code: string;
      cost_item_id: string | null;
      project_id: string | null;
      amount: string;
    }>;
  } | null;
  revenue_closing: {
    total_amount?: string;
    entries_count?: number;
    accounts?: Array<{ account_code: string; amount: string }>;
  } | null;
  quarterly_snapshot: {
    quarter?: number;
    ytd_revenue?: string;
    ytd_expenses?: string;
    ytd_financial_result?: string;
  } | null;
  annual_closing: {
    financial_result?: string;
    transfer_account?: string | null;
    transfer_amount?: string;
    entries_count?: number;
  } | null;
  /** ч.1134: закриття рахунку 443 наприкінці року. `used_profit` — дебетове
   *  сальдо 443 зі знаком: додатне — використано (вилучено) більше, ніж внесено. */
  used_profit_closing?: {
    used_profit?: string;
    from_profit?: string;
    to_loss?: string;
    covers_loss?: string;
    to_profit?: string;
    entries_count?: number;
  } | null;
}

/** ч.1120: що прибирає (або лишає) розпроведення закриття. */
export interface UnpostItem {
  /** Тип кроку або CLOSING_ENTRIES (проводки закриття). */
  kind: string;
  label: string;
  document_type: string | null;
  document_id: string | null;
  document_number: string | null;
  detail: string | null;
}

/** Попередній перегляд розпроведення. */
export interface UnpostPlan {
  removed: UnpostItem[];
  kept: UnpostItem[];
  warnings: string[];
}

/** Звіт останнього розпроведення (зберігається в документі). */
export interface UnpostReport extends UnpostPlan {
  at: string;
  by_user_id: string | null;
}

export interface MonthCloseRun {
  id: string;
  organization_id: string;
  document_number: string;
  document_date: string;
  posted: boolean;
  posted_at: string | null;
  deletion_mark: boolean;
  comment: string | null;
  period_year: number;
  period_month: number;
  status: MonthCloseStatus;
  started_at: string | null;
  completed_at: string | null;
  triggered_by_user_id: string | null;
  pre_check_passed: boolean;
  pre_check_warnings: PreCheckWarning[];
  operations_results: OperationResult[];
  closing_snapshot: ClosingSnapshot | null;
  /** ч.1120: звіт останнього розпроведення (null — закриття проведено). */
  unpost_report?: UnpostReport | null;
  /** Є кроки з помилкою — доступно «повторити невдалі кроки». */
  has_failed_steps?: boolean;
  created_at: string;
  updated_at: string;
}

export interface MonthCloseRunListItem {
  id: string;
  organization_id: string;
  document_number: string;
  document_date: string;
  period_year: number;
  period_month: number;
  status: MonthCloseStatus;
  pre_check_passed: boolean;
  posted: boolean;
  started_at: string | null;
  completed_at: string | null;
  external_source?: string | null;
}

export interface PreCheckRequest {
  organization_id: string;
  period_year: number;
  period_month: number;
}

export interface PreCheckResponse {
  passed: boolean;
  warnings: PreCheckWarning[];
  can_proceed: boolean;
}

export interface RunMonthCloseRequest {
  organization_id: string;
  period_year: number;
  period_month: number;
  skip_pre_check?: boolean;
  comment?: string | null;
}


const BASE = '/documents/month-close-runs';


export const monthCloseRuns = {
  list: (params?: {
    organization_id?: string;
    period_year?: number;
    period_month?: number;
    status?: MonthCloseStatus;
    limit?: number;
  }) =>
    api.get<Page<MonthCloseRunListItem>>(BASE, { params }).then((r) => r.data),

  get: (id: string) =>
    api.get<MonthCloseRun>(`${BASE}/${id}`).then((r) => r.data),

  remove: (id: string) =>
    api.delete(`${BASE}/${id}`).then(() => undefined),

  preCheck: (payload: PreCheckRequest) =>
    api.post<PreCheckResponse>(`${BASE}/pre-check`, payload).then((r) => r.data),

  run: (payload: RunMonthCloseRequest) =>
    api.post<MonthCloseRun>(`${BASE}/run`, payload).then((r) => r.data),

  // ч.1120: документ закриття проводять, розпроводять і проводять знову.
  /** Що прибере розпроведення і що залишить — показати до підтвердження. */
  unpostPreview: (id: string) =>
    api.get<UnpostPlan>(`${BASE}/${id}/unpost-preview`).then((r) => r.data),
  unpost: (id: string) =>
    api.post<MonthCloseRun>(`${BASE}/${id}/unpost`).then((r) => r.data),
  /** Провести знову непроведений документ. */
  post: (id: string) =>
    api.post<MonthCloseRun>(`${BASE}/${id}/post`, {}).then((r) => r.data),
  /** Розпровести і одразу провести знову. */
  repost: (id: string) =>
    api.post<MonthCloseRun>(`${BASE}/${id}/repost`, {}).then((r) => r.data),
  /** Повторити кроки, що не вдалися, і перерахувати підсумки місяця. */
  retry: (id: string) =>
    api.post<MonthCloseRun>(`${BASE}/${id}/retry`).then((r) => r.data),
};
